import { useAuth, useClaims } from '@backoffice-frontend/authentication'
import {
  AccountViewRoute,
  LOGIN_INTENDED_PATH,
  Portals,
  Routes,
  useRefContext,
} from '@backoffice-frontend/common'
import { BackofficeSignInMethod } from '@backoffice-frontend/graphql'
import {
  AccessDeniedView,
  AppBar,
  LoadingIndicator,
  NavigationDrawerContextProvider,
  useTheme,
} from '@backoffice-frontend/patterns'
import { usePageTracking } from '@backoffice-frontend/tracking'
import {
  useBackofficeLoggedInUser,
  useRefreshPermissionsSuccessToast,
} from '@backoffice-frontend/user-management-ui'
import { type ReactNode, Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Navigate,
  Route as RouterRoute,
  Routes as RouterRoutes,
  useLocation,
} from 'react-router'
import { useSentryUserContext } from '../useSentryUserContext'
import { AccountViewArea } from './AccountView'
import { MoiaHeader } from './AccountView/components/MoiaHeader'
import { SetupMFA } from './MFAView/SetupMFA'
import { NavigationDrawer } from './NavigationDrawer'
import type { BackofficeAreas } from './types/BackofficeArea'
import type { SectionsConfig } from './types/NavigationSectionsConfig'

const AreaLoadingFallback = () => <LoadingIndicator loading />

type AuthenticatedAppViewProps = {
  useNavigationSections: () => SectionsConfig
  logo: ReactNode
  logoVariant?: 'dark' | 'light'
  className?: string
}

export const AuthenticatedAppView = (props: AuthenticatedAppViewProps) => {
  const { isAuthenticated, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return null
  }

  if (!isAuthenticated) {
    const fullPath = `${location.pathname}${location.search}${location.hash}`
    localStorage.setItem(LOGIN_INTENDED_PATH, fullPath)
    return <Navigate to={Routes.Auth.Login.url} replace />
  }

  return <AuthenticatedAppViewWrapper {...props} />
}

const AuthenticatedAppViewWrapper = ({
  className,
  useNavigationSections,
  logo,
  logoVariant,
}: AuthenticatedAppViewProps) => {
  const theme = useTheme()
  const { setRef } = useRefContext()
  const appContextRef = useRef(null)
  const { hasMFA } = useAuth()
  const { user } = useBackofficeLoggedInUser()
  useRefreshPermissionsSuccessToast()
  useSentryUserContext()
  const navigationSections = useNavigationSections()

  const allAreas = navigationSections
    .flatMap(section => section?.areas)
    .filter((area): area is BackofficeAreas[number] => !!area)

  // Track page views for all authenticated routes
  usePageTracking()

  useEffect(() => {
    setRef(appContextRef)
  }, [setRef])

  const { t } = useTranslation()
  const [isOffline, setIsOffline] = useState(false)

  const checkNetworkStatus = () => {
    const handleOffline = () => {
      setIsOffline(true)
    }

    const handleOnline = () => {
      setIsOffline(false)
    }
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <auto ignored after biome migration>
  useEffect(() => checkNetworkStatus(), [])

  const claims = useClaims()
  const isSystemUser = claims.boum.isSystemUser()
  const isEmailPasswordUser =
    user?.signInMethod === BackofficeSignInMethod.EmailPassword

  // Show MFA setup if user is using email/password and has no MFA configured yet
  if (isEmailPasswordUser && !isSystemUser && !hasMFA) {
    return <SetupMFA />
  }

  return (
    <>
      {isOffline && (
        <AppBar
          css={{
            textAlign: 'center',
            top: 0,
            background: theme.palette.grey[800],

            fontColor:
              theme.semantic
                .ColorContentOnContrastCriticalSuccessDefaultAlternative,
            position: 'relative',
          }}
        >
          {t('authenticatedAppView_youAreOffline_header')}
        </AppBar>
      )}
      <div
        css={{
          height: '100vh',
          width: '100vw',
          overflow: 'hidden',
          display: 'flex',
        }}
        className={className}
      >
        <NavigationDrawerContextProvider>
          <NavigationDrawer
            navigationSections={navigationSections}
            logo={logo}
            logoVariant={logoVariant}
          />
          <div
            css={{
              flex: 1,
              position: 'relative',
            }}
          >
            <div
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <div
                ref={appContextRef}
                css={{
                  height: '100vh',
                  overflow: 'auto',
                  width: 'auto',
                }}
              >
                <MoiaHeader />
                <Suspense fallback={<AreaLoadingFallback />}>
                  <RouterRoutes>
                    <RouterRoute
                      path={AccountViewRoute.route}
                      element={<AccountViewArea />}
                    />
                    {allAreas.map(({ id, route }) => (
                      <RouterRoute
                        key={id}
                        path={`${route.path}/*`}
                        element={<route.component />}
                      />
                    ))}
                    <RouterRoute
                      path="/"
                      element={<Navigate to={'/welcome'} />}
                    />

                    <RouterRoute
                      path="*"
                      element={
                        <AccessDeniedView
                          pageName={t(
                            'authenticatedAppView_pageNotFound_header',
                          )}
                        />
                      }
                    />
                  </RouterRoutes>
                </Suspense>
                <div id={Portals.DedicatedActionButtonGroup} />
              </div>
            </div>
          </div>
        </NavigationDrawerContextProvider>
      </div>
    </>
  )
}
